import {
	CollapsibleListItemContext,
	CollapsibleListItemRenderMode
} from "../../CollapsibleList/CollapsibleList";
import {
	PropsWithChildren,
	ReactElement,
	RefObject,
	useCallback,
	useContext,
	useRef,
	useState,
	KeyboardEvent
} from "react";
import {
	FontIconName,
	Icon,
	SvgIconValue
} from "../../Icon/Icon";
import { DropOverActionList } from "../../DropOverActionList/DropOverActionList";
import { ActionListHeader } from "../../ActionList/ActionListHeader";
import { ActionListDivider } from "../../ActionList/ActionListDivider";
import { ButtonBase } from "../../ButtonBase/ButtonBase";
import styles from "./ActionBarMenuButton.module.less";
import { ActionBarButtonIcon } from "../ActionBarButtonIcon/ActionBarButtonIcon";
import { generateTrackingId } from "client/Misc/utils";
import {
	KeyBoardListNavigationContext,
	useKeyboardListNavigation,
	useKeyboardListNavigationItem
} from "client/Hooks/useKeyboardListNavigation";
import { Key } from "client/Misc/commonTypes";
import { mergeRefs } from "client/Misc/reactUtils";

export type ActionBarMenuButtonProps = {
	icon?: FontIconName | SvgIconValue;
	text: string;
	trackingId?: string;
};

export const ActionBarMenuButton = (props: PropsWithChildren<ActionBarMenuButtonProps>): ReactElement => {
	const { children, text, icon, trackingId } = props;
	const {
		renderMode,
		index,
		tabIndex
	} = useContext(CollapsibleListItemContext);

	if (renderMode === CollapsibleListItemRenderMode.Collapsed) {
		return (
			<>
				<ActionListDivider />
				<ActionListHeader text={text} trackingId={trackingId} />
				{children}
				<ActionListDivider />
			</>
		);
	}

	return (
		<NormalButton
			trackingId={trackingId}
			icon={icon}
			text={text}
			index={index}
			tabIndex={tabIndex}
		>
			{children}
		</NormalButton>
	);
};

ActionBarMenuButton.displayName = "ActionBarMenuButton";

type NormalButtonProps = {
	trackingId?: string;
	icon?: FontIconName | SvgIconValue;
	text: string;
	index: number;
	tabIndex: number;
};

const NormalButton = (props: PropsWithChildren<NormalButtonProps>): ReactElement => {
	const {
		trackingId,
		icon,
		text,
		children,
		index,
		tabIndex
	} = props;

	const [isOpen, setIsOpen] = useState(false);
	const ref: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const toggleOpen = useCallback(
		(): void => {
			setIsOpen(isOpen => !isOpen);
		},
		[]
	);

	const cancelHandler = useCallback(
		(): void => {
			ref.current
				?.focus();
			setIsOpen(false);
		},
		[]
	);

	const {
		ref: horizontalKeyboardNavRef,
		onKeyDown
	} = useKeyboardListNavigationItem({
		itemIndex: index
	});

	const {
		contextData: keyboardNavigationContext,
		focusFirstItemHandler: dropOverListFocusFirstItemHandler
	} = useKeyboardListNavigation({
		shouldCancelDirectlyFromItems: true,
		onCancel: cancelHandler,
		onFocusInput: () => {
			ref.current
				?.focus();
		}
	});

	const keyDownHandler = useCallback(
		(event: KeyboardEvent<HTMLElement>): void => {
			switch (event.key) {
				case Key.ArrowDown:
				case Key.Enter:
				case Key.Space:
					setIsOpen(true);

					// Ensure the dropdown is mounted before focusing an item
					setTimeout(
						(): void => {
							dropOverListFocusFirstItemHandler();
						},
						1
					);

					event.preventDefault();
					break;
				case Key.Escape:
					case Key.ArrowUp:
						setIsOpen(false);
						event.preventDefault();
					break;
				case Key.Tab:
					if (isOpen) {
						setIsOpen(false);
						event.preventDefault();
					}
					break;
				case Key.ArrowLeft:
				case Key.ArrowRight:
					setIsOpen(false);
					onKeyDown(event);
					break;
				default:
					onKeyDown(event);
			}
		},
		[
			dropOverListFocusFirstItemHandler,
			onKeyDown,
			isOpen
		]
	);

	return (
		<>
			<ButtonBase
				ref={mergeRefs(ref, horizontalKeyboardNavRef)}
				className={styles.root}
				onClick={toggleOpen}
				trackingId={trackingId}
				onKeyDown={keyDownHandler}
				tabIndex={tabIndex}
			>
				{icon && (
					<ActionBarButtonIcon icon={icon} />
				)}
				<span>
					{text}
				</span>
				<span>
					<Icon value="chevron-down" />
				</span>
			</ButtonBase>

			{ref.current && isOpen && children && (
				<DropOverActionList
					target={ref.current}
					targetAlignment="bl"
					contentAlignment="tl"
					onClose={() => setIsOpen(false)}
					trackingId={generateTrackingId(trackingId, "__dropover")}
					menuTitle={text}
				>
					<KeyBoardListNavigationContext.Provider value={keyboardNavigationContext}>
						{children}
					</KeyBoardListNavigationContext.Provider>
				</DropOverActionList>
			)}
		</>
	);
};
NormalButton.displayName = "NormalButton";
